// ─── Dashboard Type ──────────────────────────────────────────────────────────

export type DashboardType = 'business' | 'employee' | 'employee-portal' | 'franchise';

// ─── Routes ──────────────────────────────────────────────────────────────────

export const DASHBOARD_ROUTES: Record<DashboardType, string> = {
  business: '/dashboard',
  employee: '/employee-dashboard',
  'employee-portal': '/employee-portal',
  franchise: '/portfolio',
};

export function getDashboardTypeFromPath(pathname: string): DashboardType | null {
  if (pathname.startsWith('/employee-dashboard')) return 'employee';
  if (pathname.startsWith('/employee-portal')) return 'employee-portal';
  if (pathname.startsWith('/dashboard')) return 'business';
  if (pathname.startsWith('/portfolio')) return 'franchise';
  return null;
}

// ─── Business Tab IDs ────────────────────────────────────────────────────────

export const BusinessTabs = {
  OVERVIEW: 'overview',
  REVIEWS: 'reviews',
  INSIGHTS: 'insights',
  FORMS: 'forms',
  GOOGLE: 'google',
  FEEDBACK_INTELLIGENCE: 'feedback-intelligence',
  CONTACTS: 'contacts',
  CONTACTS_TAGS: 'contacts-tags',
  CONTACTS_BUSYNESS: 'contacts-busyness',
  CONTACTS_IMPORT_EXPORT: 'contacts-import-export',
  LOYALTY: 'loyalty',
  COUPONS: 'coupons',
  WALLET_STATISTICS: 'wallet-statistics',
  BULK_MAIL: 'bulk-mail',
  EMAIL_TEMPLATES: 'email-templates',
  SOCIAL_ACCOUNTS: 'social-accounts',
  SOCIAL_POSTS: 'social-posts',
  SOCIAL_INBOX: 'social-inbox',
  SOCIAL_ANALYTICS: 'social-analytics',
  SOCIAL_AUTO_REPLY: 'social-auto-reply',
  SIGNAGE: 'signage',
} as const;

// ─── Employee Tab IDs ────────────────────────────────────────────────────────

export const EmployeeTabs = {
  INSIGHTS: 'insights',
  TIME_TRACKING: 'time-tracking',
  SHIFT_MANAGEMENT: 'shift-management',
  PAYROLL: 'payroll',
  FINANCIAL: 'financial',
  EMPLOYEE_LIST: 'employee-list',
  MEETINGS: 'meetings',
  MEETINGS_CALENDAR: 'meetings-calendar',
  MEETINGS_PAGES: 'meetings-pages',
  COMMERCE: 'commerce',
  COMMERCE_CATALOG: 'commerce-catalog',
  COMMERCE_SERVICES: 'commerce-services',
  COMMERCE_MENU: 'commerce-menu',
  COMMERCE_BOOKINGS: 'commerce-bookings',
  COMMERCE_INVENTORY: 'commerce-inventory',
  EVENTS: 'events',
  JOB_POSTINGS: 'job-postings',
  COMMUNICATIONS: 'communications',
} as const;

// ─── Employee Portal Tab IDs ─────────────────────────────────────────────────

export const PortalTabs = {
  MY_DASHBOARD: 'my-dashboard',
  MY_SHIFTS: 'my-shifts',
  MY_PAYROLL: 'my-payroll',
  MY_EVENTS: 'my-events',
  CONVERSATIONS: 'conversations',
} as const;

// ─── Franchise Tab IDs ───────────────────────────────────────────────────────

export const FranchiseTabs = {
  OVERVIEW: 'overview',
  EMPLOYEES: 'employees',
  SCHEDULE: 'schedule',
  ANALYTICS: 'analytics',
  PAYROLL: 'payroll',
  ALERTS: 'alerts',
} as const;

// ─── Tab Order (for keyboard cycling via Ctrl+Up/Down) ───────────────────────
// contacts-import-export excluded (disabled), financial excluded (no keyboard cycling)

export const BUSINESS_TAB_ORDER = [
  BusinessTabs.OVERVIEW,
  BusinessTabs.REVIEWS, BusinessTabs.INSIGHTS, BusinessTabs.FORMS, BusinessTabs.GOOGLE, BusinessTabs.FEEDBACK_INTELLIGENCE,
  BusinessTabs.CONTACTS, BusinessTabs.CONTACTS_TAGS, BusinessTabs.CONTACTS_BUSYNESS,
  BusinessTabs.LOYALTY, BusinessTabs.COUPONS, BusinessTabs.WALLET_STATISTICS,
  BusinessTabs.BULK_MAIL, BusinessTabs.EMAIL_TEMPLATES,
  BusinessTabs.SOCIAL_ACCOUNTS, BusinessTabs.SOCIAL_POSTS, BusinessTabs.SOCIAL_INBOX, BusinessTabs.SOCIAL_ANALYTICS, BusinessTabs.SOCIAL_AUTO_REPLY,
  BusinessTabs.SIGNAGE,
] as const;

export const EMPLOYEE_TAB_ORDER = [
  EmployeeTabs.INSIGHTS, EmployeeTabs.TIME_TRACKING, EmployeeTabs.SHIFT_MANAGEMENT,
  EmployeeTabs.PAYROLL, EmployeeTabs.EMPLOYEE_LIST, EmployeeTabs.COMMERCE_CATALOG,
  EmployeeTabs.COMMUNICATIONS,
] as const;

export const FRANCHISE_TAB_ORDER = [
  FranchiseTabs.OVERVIEW, FranchiseTabs.EMPLOYEES, FranchiseTabs.SCHEDULE,
  FranchiseTabs.ANALYTICS, FranchiseTabs.PAYROLL, FranchiseTabs.ALERTS,
] as const;

// ─── Default Tabs ────────────────────────────────────────────────────────────

export const DEFAULT_TABS: Record<DashboardType, string> = {
  business: BusinessTabs.OVERVIEW,
  employee: EmployeeTabs.INSIGHTS,
  'employee-portal': PortalTabs.MY_DASHBOARD,
  franchise: FranchiseTabs.OVERVIEW,
};

/** Tabs a `free` plan may open. Any other plan name, including a plan that has not loaded, allows every tab. */
export const FREE_PLAN_TABS: Partial<Record<DashboardType, ReadonlySet<string>>> = {
  business: new Set([
    BusinessTabs.OVERVIEW,
    BusinessTabs.REVIEWS,
    BusinessTabs.FORMS,
    BusinessTabs.LOYALTY,
    BusinessTabs.COUPONS,
    BusinessTabs.WALLET_STATISTICS,
  ]),
  employee: new Set([
    EmployeeTabs.INSIGHTS,
    EmployeeTabs.SHIFT_MANAGEMENT,
    EmployeeTabs.EMPLOYEE_LIST,
    EmployeeTabs.TIME_TRACKING,
  ]),
};

/** Portfolio is for accounts that already have several businesses. The free plan never opens it. */
export function canOpenPortfolio(planName: string | null | undefined, businessCount: number): boolean {
  return planName !== 'free' && businessCount > 1;
}

export function isTabAllowedForPlan(
  planName: string | null | undefined,
  dashboard: DashboardType,
  tab: string,
): boolean {
  if (planName !== 'free') return true;
  const allowed = FREE_PLAN_TABS[dashboard];
  if (!allowed) return true;
  return allowed.has(tab);
}

// ─── Section Aliases (AI navigation: friendly name → canonical tab ID) ───────

export const BUSINESS_SECTION_ALIASES: Record<string, string> = {
  reviews: BusinessTabs.REVIEWS,
  forms: BusinessTabs.FORMS,
  'review-forms': BusinessTabs.FORMS,
  loyalty: BusinessTabs.LOYALTY,
  analytics: BusinessTabs.INSIGHTS,
  insights: BusinessTabs.INSIGHTS,
  customers: BusinessTabs.CONTACTS,
  signage: BusinessTabs.SIGNAGE,
  'bulk-mail': BusinessTabs.BULK_MAIL,
  'google-reviews': BusinessTabs.GOOGLE,
  google: BusinessTabs.GOOGLE,
  coupons: BusinessTabs.COUPONS,
  'email-templates': BusinessTabs.EMAIL_TEMPLATES,
};

export const EMPLOYEE_SECTION_ALIASES: Record<string, string> = {
  employees: EmployeeTabs.EMPLOYEE_LIST,
  shifts: EmployeeTabs.SHIFT_MANAGEMENT,
  scheduling: EmployeeTabs.SHIFT_MANAGEMENT,
  payroll: EmployeeTabs.PAYROLL,
  'time-tracking': EmployeeTabs.TIME_TRACKING,
  communications: EmployeeTabs.COMMUNICATIONS,
  commerce: EmployeeTabs.COMMERCE_CATALOG,
  products: EmployeeTabs.COMMERCE_CATALOG,
  catalog: EmployeeTabs.COMMERCE_CATALOG,
  services: EmployeeTabs.COMMERCE_SERVICES,
  bookings: EmployeeTabs.COMMERCE_BOOKINGS,
  inventory: EmployeeTabs.COMMERCE_INVENTORY,
  menu: EmployeeTabs.COMMERCE_MENU,
};
